import { Card as RadixCard } from '@radix-ui/themes';
import { Flex, Text } from '@/components/ui';

import type { ComponentPropsWithoutRef } from 'react';
import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

type RadixCardProps = ComponentPropsWithoutRef<typeof RadixCard>;

export type CardSize = 'xsmall' | 'small' | 'medium' | 'large';
export type CardVariant = 'surface' | 'classic' | 'ghost';
export type CardColor =
  | 'gray'
  | 'blue'
  | 'red'
  | 'green'
  | 'teal'
  | 'ruby'
  | 'amber'
  | 'orange'
  | 'crimson'
  | 'jade'
  | 'pink'
  | 'plum'
  | 'purple'
  | 'violet'
  | 'indigo';

interface BaseCardProps {
  size?: CardSize | 'none';
  variant?: CardVariant;
  asChild?: boolean;
  className?: string;
  children?: ReactNode;
  /**
   * Disables padding for the card. Adds the class 'card--no-padding'.
   */
  disablePadding?: boolean;
  /**
   * Sets the background color of the card via CSS variable --card-background-color.
   * Can be any valid CSS color value (hex, rgb, rgba, color name, CSS variable, etc.).
   */
  background?: string;
}

export type CardProps = Omit<RadixCardProps, 'size' | 'variant'> &
  BaseCardProps &
  HTMLAttributes<HTMLDivElement>;

const sizeMapping = {
  xsmall: '1',
  small: '2',
  medium: '3',
  large: '4',
  none: '1',
} as const;

const variantMapping = {
  surface: 'surface',
  classic: 'classic',
  ghost: 'ghost',
} as const;

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      size = 'medium',
      variant = 'surface',
      asChild = false,
      className,
      children,
      disablePadding = false,
      background,
      style,
      ...props
    },
    ref
  ) => {
    const radixSize = sizeMapping[size];
    const radixVariant = variantMapping[variant];
    const computedClassName = clsx(
      'card',
      `card-size-${size}`,
      `card-variant-${variant}`,
      disablePadding && 'card--no-padding',
      className
    );

    const computedStyle = {
      ...(background && {
        '--card-background-color': background,
        backgroundColor: 'var(--card-background-color)',
      }),
      ...style,
    } as React.CSSProperties;

    return (
      <RadixCard
        ref={ref}
        size={radixSize as RadixCardProps['size']}
        variant={radixVariant as RadixCardProps['variant']}
        asChild={asChild}
        className={computedClassName}
        style={computedStyle}
        {...props}
      >
        {children}
      </RadixCard>
    );
  }
);
Card.displayName = 'Card';

export type CardHeaderSize = 'small' | 'medium' | 'large';

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  cardTitle?: string | ReactNode;
  subtitle?: string;
  tagline?: string;
  description?: string;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  className?: string;
  size?: CardHeaderSize;
  divider?: boolean;
}

/**
 * CardHeader component for displaying card titles, subtitles, tagline, and optional slots.
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  (
    {
      cardTitle,
      subtitle,
      tagline,
      description,
      startSlot,
      endSlot,
      className,
      size = 'medium',
      divider = false,
      children,
      ...rest
    },
    ref
  ) => {
    // If children are provided, use the simple version
    if (children) {
      return (
        <div ref={ref} className={clsx('card__header', className)} {...rest}>
          {children}
        </div>
      );
    }

    // Use the structured version with cardTitle, subtitle, etc.
    return (
      <Flex
        ref={ref}
        className={clsx(
          'card__header',
          `card__header--size-${size}`,
          divider && 'card__header--with-divider',
          className
        )}
        {...rest}
      >
        {startSlot && (
          <div className="card__header__start-slot">{startSlot}</div>
        )}
        <Flex direction="column" className="card__header__content">
          {tagline && (
            <Text size="tagline" className="card__header__tagline">
              {tagline}
            </Text>
          )}
          {cardTitle && (
            <div
              className={`card__header__title card__header__title--size-${size}`}
            >
              {cardTitle}
            </div>
          )}
          {subtitle && <div className="card__header__subtitle">{subtitle}</div>}
          {description && (
            <div className="card__header__description">{description}</div>
          )}
        </Flex>
        {endSlot && <div className="card__header__end-slot">{endSlot}</div>}
      </Flex>
    );
  }
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx('card__title', className)} {...props} />
));
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx('card__description', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx('card__content', className)} {...props} />
));
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx('card__footer', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';
